import React, { ReactNode } from 'react';
import '../../../styles/RouteSeoContent.css';

/**
 * Curated long-form content sections for the Bangalore (SBC) -> Chennai (MAS)
 * route page. Rendered between the train listing content and the FAQ section.
 *
 * Scoped to a single route (see gating in RouteInfo) so the hand-written
 * Bangalore-Chennai copy never appears on other route pages that share the
 * generic RouteInfo component. Purely presentational — no JS logic, no data
 * fetching, no changes to existing sections.
 */

type SectionProps = {
  id: string;
  title: string;
  children: ReactNode;
};

const Section = ({ id, title, children }: SectionProps) => (
  <section id={id} className="seoContentSection" itemScope itemType="https://schema.org/WebPageElement">
    <h2 className="font22 latoBold appendBottom15" itemProp="name">
      {title}
    </h2>
    <div itemProp="description">{children}</div>
  </section>
);

const Table = ({ headers, rows }: { headers: string[]; rows: string[][] }) => (
  <div className="seoContentTableWrapper">
    <table className="seoContentTable">
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => (
              <td key={ci}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const List = ({ items }: { items: string[] }) => (
  <ul>
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const RouteSeoContent = () => (
  <article className="seoContent" itemScope itemType="https://schema.org/Article" itemProp="articleBody">
    {/* SECTION 1 — Fare Range By Class */}
    <Section id="fare-range" title="Bangalore to Chennai Train Fare Range By Class">
      <p>
        Bangalore to Chennai train fares span a wide range, from ₹130 in General/2S up to ₹5,500 in First AC, depending
        on class and comfort level. Budget and overnight travellers typically choose Sleeper or 3A (₹230–₹1,100), while
        daytime and business travellers opt for Chair Car or Executive CC (₹550–₹2,000). Second AC and First AC sit at
        the premium end, favoured by families and those wanting private, high-comfort overnight journeys.
      </p>
      <Table
        headers={['Class', 'Fare Range', 'Recommended For']}
        rows={[
          ['General / 2S', '₹130–₹170', 'Budget trips'],
          ['Sleeper (SL)', '₹230–₹900', 'Budget overnight travel'],
          ['Third AC (3A)', '₹625–₹1,100', 'Students, solo travellers'],
          ['Second AC (2A)', '₹880–₹2,000', 'Families, business travellers'],
          ['Chair Car (CC)', '₹550–₹1,400', 'Daytime travel'],
          ['Executive CC (EC)', '₹1,500–₹2,000', 'Premium day travel'],
          ['First AC (1A)', '₹4,000–₹5,500', 'Luxury, premium overnight']
        ]}
      />
    </Section>

    {/* SECTION 2 — Train Classes Comparison */}
    <Section id="train-classes" title="Bangalore to Chennai Train Classes Comparison">
      <p>
        Budget and overnight travellers typically go for Sleeper or 3A, while daytime travellers prefer Chair Car or
        Executive CC, for AC seating, without an overnight stay. 2A and 1A sit at the premium end, suited to families
        and couples wanting higher comfort or private cabins.
      </p>
      <Table
        headers={['Class', 'Comfort Level', 'Recommended For']}
        rows={[
          ['1A (First AC)', 'Premium private AC cabin', 'Couples, luxury, premium travel'],
          ['2A (Second AC)', 'High comfort AC berths', 'Families, business travellers'],
          ['3A (Third AC)', 'Budget AC berths', 'Students, solo travellers'],
          ['SL (Sleeper)', 'Non-AC berths', 'Budget passengers, overnight'],
          ['CC (Chair Car)', 'AC seating', 'Daytime travel, office trips'],
          ['EC (Executive CC)', 'Premium AC seating', 'Business travellers, premium day']
        ]}
      />
    </Section>

    {/* SECTION 3 — Cancellation and Refund Guide */}
    <Section id="cancellation-refund" title="Cancellation and Refund Guide">
      <p>
        The closer you cancel to your departure date, the less refund you get. A full refund, minus a flat charge
        (₹60–₹180 by class) if you cancel 48+ hours before chart preparation, dropping to 75% (12–48 hrs), 50% (4–12
        hrs) before chart preparation, and nothing within 4 hours or after chart preparation. If your waitlisted ticket
        doesn&apos;t get confirmed, it&apos;s automatically cancelled and refunded, minus a small fee per passenger,
        while Tatkal tickets are non-refundable unless IRCTC itself cancels the train.
      </p>
      <Table
        headers={['When You Cancel', 'Refund for Confirmed Ticket']}
        rows={[
          ['More than 48 hours before departure', 'Full refund minus flat cancellation charge'],
          ['12–48 hours before departure', '75% refund (25% deducted)'],
          ['4–12 hours before departure', '50% refund (50% deducted)'],
          ['Less than 4 hours / after chart prep', 'No refund'],
          ['Waitlisted e-ticket (auto cancelled)', 'Full refund minus ₹20–₹60 per passenger'],
          ['Tatkal ticket cancellation', 'No refund']
        ]}
      />
    </Section>

    {/* SECTION 4 — Tatkal Ticket Availability */}
    <Section id="tatkal" title="Bangalore to Chennai Tatkal Ticket Availability">
      <p>
        Tatkal booking opens one day before departure — 10:00 AM for AC classes and 11:00 AM for Sleeper/General — with
        extra charges over the base fare varying by class. Demand peaks on Fridays, Sundays, and around festivals/long
        weekends, so Tuesday–Wednesday departures offer better confirmation chances.
      </p>
      <Table
        headers={['Detail', 'Information']}
        rows={[
          ['AC Classes Opens at?', '10:00 AM (2A, 3A, CC, EC, 3E)'],
          ['Non-AC Opens at?', '11:00 AM (Sleeper, General)'],
          ['Tatkal Booking Window', '1 day before departure from originating station'],
          ['Tatkal Peak Demand', 'Fridays, Sundays, festivals, long weekends'],
          ['Additional Charges for Tatkal bookings?', 'Tatkal charges apply over base fare, vary by class']
        ]}
      />
    </Section>

    {/* SECTION 5 — Waitlist & Confirmation Guide */}
    <Section id="waitlist-guide" title="Bangalore to Chennai - Waitlist &amp; Confirmation Guide">
      <h3>Waitlist Types</h3>
      <p>
        GNWL offers the best confirmation odds (60–85%) and should be your first choice, with PQWL as a reasonable
        backup if GNWL isn&apos;t available. RAC almost always lets you travel even unconfirmed, while RLWL has very low
        confirmation chances and is best avoided.
      </p>
      <Table
        headers={['Waitlist Type', 'Confirmation Chance', 'Should You Book?']}
        rows={[
          ['GNWL', '60–85%', 'Yes — best option'],
          ['PQWL', 'Moderate', 'Yes — if GNWL gone'],
          ['RLWL', 'Very low', 'Avoid if possible'],
          ['RAC', 'Very high', 'Yes — travel allowed']
        ]}
      />
    </Section>

    {/* SECTION 6 — Boarding Station Guide */}
    <Section id="boarding-guide" title="Bangalore to Chennai — Boarding Station Guide">
      <p>Bangalore has 4 boarding stations:</p>
      <List
        items={[
          'KSR Bengaluru City Junction (SBC) — Primary terminal',
          'Yesvantpur Junction (YPR) — North Bangalore hub',
          'Krishnarajapuram Junction (KJM) — East Bangalore hub',
          'Bengaluru Cantonment (BNC) — Central-East Bangalore'
        ]}
      />
      <p>
        SBC suits South/Central Bangalore and travellers wanting maximum train options and amenities, KJM is best for
        East Bangalore (Whitefield, KR Puram, Marathahalli) since it cuts 1–2 hours of city traffic, YPR works for
        North/Northwest Bangalore, and BNC is a fallback only for the few trains that specifically halt there. SBC has
        the most departures and full facilities but heavy peak-hour traffic; KJM, YPR, and BNC trade off fewer train
        options and smaller amenities for lower congestion and faster access from their respective zones.
      </p>

      <h3>Which Boarding Station Is Best for You?</h3>
      <Table
        headers={['Your Area in Bangalore', 'Best Station', 'Why is it preferred?']}
        rows={[
          ['Whitefield / ITPL', 'KJM', 'Saves 1–2 hours of city traffic to SBC'],
          ['KR Puram / Mahadevapura', 'KJM', 'Closest major station on Chennai route'],
          ['Marathahalli / Bellandur', 'KJM', 'Faster access via Outer Ring Road'],
          ['Indiranagar / HAL', 'KJM or BNC', 'East-side access, avoids Majestic area'],
          ['Hebbal / Yelahanka', 'YPR', 'Closer approach from North Bangalore'],
          ['Yeshwanthpur / Tumkur Road', 'YPR', 'Station is in the neighbourhood'],
          ['Rajajinagar / Malleshwaram', 'SBC or YPR', 'Both accessible, SBC has more trains'],
          ['Jayanagar / BTM / JP Nagar', 'SBC', 'South Bangalore — SBC is nearest hub'],
          ['Electronic City / Hosur Rd', 'SBC', 'Best direct connectivity'],
          ['MG Road / Shivajinagar', 'SBC', 'Central Bangalore, metro accessible']
        ]}
      />

      <h3>Can You Board from KJM Instead of SBC?</h3>
      <p>
        Yes — KJM is a scheduled stop on most Bangalore–Chennai trains, so you can board there instead of SBC. Just
        request a boarding point change on Makemytrip up to 24 hours before departure. The main exceptions are faster,
        fewer-stop trains (like the Shatabdi and the standard Chennai Mail route).
      </p>
      <p>
        Four trains stop at KJM daily — Kaveri Express (00:09 AM), Lalbagh SF Express (06:50 AM), Brindavan SF Express
        (15:34), and MAS Double Decker (13:54) — covering early morning, mid-morning, and afternoon departures. The
        Mysuru–MGR Chennai Weekly SF Express adds a Wednesday-only option at 11:23 PM for travellers needing extra
        availability.
      </p>
    </Section>

    {/* SECTION 7 — Chennai Arrival Station Guide */}
    <Section id="arrival-guide" title="Chennai Arrival Station Guide — MAS vs MS">
      <p>
        Chennai has 2 Arrival stations: MAS (MGR Chennai Central) — Most daily premium trains · MS (Chennai Egmore) —
        Select weekly and special trains
      </p>
      <p>
        MAS (Chennai Central) is the better arrival station for most destinations — T Nagar, Anna Nagar, OMR, the
        airport, and Parrys — thanks to metro connectivity and central access. MS (Egmore) works better for South
        Chennai destinations like Tambaram, Velachery, and GST Road, plus it&apos;s a direct walk for Egmore itself.
        Adyar and Besant Nagar are reachable from either station with similar ease.
      </p>

      <h3>Which Chennai Station Is Better for You?</h3>
      <Table
        headers={['Your Destination in Chennai', 'Better Station', 'Why is it preferred?']}
        rows={[
          ['T Nagar / Nungambakkam', 'MAS', 'Metro + cab access'],
          ['Anna Nagar / Kilpauk', 'MAS', 'North Chennai connectivity'],
          ['OMR / IT Corridor', 'MAS', 'Metro to Taramani/Sholinganallur'],
          ['Chennai Airport', 'MAS', 'Direct Metro interchange'],
          ['Parrys / George Town', 'MAS', 'Central business district'],
          ['Tambaram / Chromepet', 'MS', 'South line suburban trains'],
          ['Velachery / Guindy', 'MS', 'South Chennai suburbs'],
          ['GST Road / Perungalathur', 'MS', 'Better south approach'],
          ['Adyar / Besant Nagar', 'MAS or MS', 'Either works — cab from both'],
          ['Egmore / Vepery', 'MS', 'Walking distance'],
          ['Central / Broadway', 'MAS', 'Walking distance']
        ]}
      />
    </Section>

    {/* SECTION 8 — Fastest Trains */}
    <Section id="fastest-trains" title="Fastest Trains (Shatabdi Express and Vande Bharat Express)">
      <p>
        The two Shatabdi services (12028 at 06:00, 12008 at 16:10) cover the Bangalore-Chennai route in 5–5h 15m, both
        offering CC and EC classes with fares roughly ₹925–₹1,990. The MAS Vande Bharat Express (20608, 14:50) is the
        fastest train on this route at 4h 30m and the only Vande Bharat service running between Bangalore and Chennai,
        making it the top pick for daytime business travel.
      </p>
      <Table
        headers={['Train', 'No.', 'Departure', 'Arrival', 'Duration', 'Classes / Fare']}
        rows={[
          ['Shatabdi Express', '12028', '06:00 (SBC)', '11:00 (MAS)', '5h 00m', 'CC–EC, ₹945–₹1,535'],
          ['Shatabdi Express', '12008', '16:10 (SBC)', '21:25 (MAS)', '5h 15m', 'CC–EC, ₹925–₹1,990'],
          [
            'MAS Vande Bharat Express',
            '20608',
            '14:50 (SBC)',
            '19:20 (MAS)',
            '4h 30m',
            'CC, EC — fastest train on the route, daily, best for daytime business travel'
          ]
        ]}
      />
    </Section>

    {/* SECTION 9 — Best Morning Trains */}
    <Section id="morning-trains" title="Best Morning Trains from Bangalore to Chennai">
      <p>
        Morning departures (before 10 AM) from Bangalore to Chennai range from 4h 45m to 6h 40m, with the Vande Bharat
        (20663) being fastest at 4h 45m and Shatabdi (12028) close behind at 5h. For comfort, Lalbagh SF Express and
        Sanghamitra Express offer the widest class range including Sleeper and AC options, while Shatabdi and Vande
        Bharat stick to Chair Car/Executive Chair Car for quicker, premium daytime travel.
      </p>
      <Table
        headers={['Train Name', 'No.', 'Departure', 'Arrival', 'Duration', 'Classes']}
        rows={[
          ['Shatabdi Express', '12028', '06:00', '11:00', '5h 00m', 'CC, EC'],
          ['Lalbagh SF Express', '12608', '06:25', '12:20', '5h 55m', '2A, 3A, SL, CC, 2S'],
          ['MAS Vande Bharat', '20663', '07:45', '12:30', '4h 45m', 'CC, EC'],
          ['Chennai Express', '16552', '07:55', '14:35', '6h 40m', '2S, CC'],
          ['Sanghamitra Express', '12295', '09:00', '15:15', '6h 15m', '2A, 3A, SL'],
          ['SMVB KYQ AC Express', '12551', '09:30', '14:40', '5h 10m', '2A, 3A']
        ]}
      />
    </Section>

    {/* SECTION 10 — Best Night Trains */}
    <Section id="night-trains" title="Best Night Trains from Bangalore to Chennai">
      <p>
        For overnight travel, the SBC–Chennai SF Mail (12658, 22:40–04:05) which runs daily, is the fastest option at 5h
        25m, while the Kaveri Express (16022, 23:50–06:45) offers a more relaxed overnight schedule with a longer sleep
        window. The Mysuru–Chennai Weekly SF (22681) runs only on Wednesdays and serves as a useful backup when the
        daily trains are full.
      </p>
      <Table
        headers={['Train Name', 'No.', 'Departure', 'Arrival', 'Duration', 'Frequency', 'Best For']}
        rows={[
          ['KSR Bengaluru – Chennai SF Mail', '12658', '22:40', '04:05', '5h 25m', 'Daily', 'Fastest overnight travel'],
          ['Kaveri Express', '16022', '23:50', '06:45', '6h 55m', 'Daily', 'Comfortable overnight sleep'],
          ['Mysuru – Chennai Weekly SF', '22681', '23:00', '05:15', '6h 15m', 'Weekly (Wed)', 'Extra availability']
        ]}
      />
    </Section>
  </article>
);

export default RouteSeoContent;
